/**
 * BU-v2 P3-3 unit switcher — client persistence of the caller's active
 * "create into" operating unit.
 *
 * Kept as a dependency-free localStorage projection (NOT the Zustand UI store)
 * so the API client can read it without an import cycle. Stored per-user
 * ({userId, unitId}) so a shared machine never leaks one user's selection to
 * the next. The backend ALWAYS re-validates the sent unit against the caller's
 * reach — this is a convenience/UX seam, never an authority.
 *
 * Workspace-entry: the selection is additionally SESSION-SCOPED. Every real
 * login rotates a login-epoch marker (rotateLoginEpoch — called by the token
 * install path, never by silent token refresh), and a pick stamped with an
 * older epoch simply doesn't count. That is the whole "every session starts
 * with a choice" mechanism: the entry gate sees no valid pick after a fresh
 * login and routes multi-unit members to the chooser, while reloads and new
 * tabs inside the same session sail straight through.
 */
const KEY = 'crm_active_unit';
const EPOCH_KEY = 'crm_login_epoch';

export interface ActiveUnitSelection {
  userId: string;
  unitId: string;
  /** Login epoch the pick was made in; picks from older epochs are stale. */
  epoch?: string;
}

function newEpoch(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/** The current login epoch, or null when storage is unavailable. */
export function currentLoginEpoch(): string | null {
  try {
    return localStorage.getItem(EPOCH_KEY);
  } catch {
    return null;
  }
}

/**
 * Start a new login epoch. Called ONLY when a session is genuinely installed
 * (login, MFA verify, SSO/passkey/magic-link success, impersonation switch) —
 * NEVER on silent token refresh, which would turn "ask once per login" into
 * "ask every fifteen minutes".
 */
export function rotateLoginEpoch(): void {
  try {
    localStorage.setItem(EPOCH_KEY, newEpoch());
  } catch {
    /* storage unavailable — picks will read as stale, which fails safe */
  }
  notify();
}

export function readActiveUnit(): ActiveUnitSelection | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveUnitSelection>;
    if (!parsed?.userId || !parsed?.unitId) return null;
    // Session scoping: a pick from a previous login epoch is not a pick.
    // (Both the UI and the API client read through here, so the screen and
    // the X-CRM-Active-Unit header can never disagree about staleness.)
    if (parsed.epoch !== (currentLoginEpoch() ?? undefined)) return null;
    return {
      userId: parsed.userId,
      unitId: parsed.unitId,
      ...(parsed.epoch && { epoch: parsed.epoch }),
    };
  } catch {
    return null;
  }
}

// Cross-instance reactivity: every useSelectedUnit consumer (the header
// breadcrumb, the line hook, the products/leads lists…) must re-read when ANY
// of them changes the selection. A per-component tick can't do that, so we
// expose a tiny external store (useSyncExternalStore) that bumps on every write.
let version = 0;
const listeners = new Set<() => void>();

function notify(): void {
  version += 1;
  listeners.forEach((l) => l());
}

// Cross-TAB reactivity: the API client reads localStorage fresh on every
// request, so a switch in tab A immediately changes what tab B *sends* — but
// without this bridge tab B's UI would keep displaying the old unit (the
// `storage` event fires only in OTHER tabs, never the writing one). Screen and
// header must never disagree: a create would silently land in the wrong unit.
// A login in another tab (epoch rotation) invalidates this tab's pick the same
// way, so it also notifies.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY || e.key === EPOCH_KEY || e.key === null) notify();
  });
}

export function subscribeActiveUnit(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getActiveUnitVersion(): number {
  return version;
}

export function writeActiveUnit(selection: ActiveUnitSelection | null): void {
  try {
    if (selection) {
      // Stamp with the current epoch so the pick lives exactly as long as the
      // login session. No epoch yet (legacy session mid-deploy) → stamp one
      // now so the pick is at least valid for the remainder of this session.
      let epoch = currentLoginEpoch();
      if (!epoch) {
        rotateLoginEpoch();
        epoch = currentLoginEpoch();
      }
      localStorage.setItem(KEY, JSON.stringify({ ...selection, ...(epoch && { epoch }) }));
    } else {
      localStorage.removeItem(KEY);
    }
  } catch {
    /* storage unavailable (private mode) — the header is optional, so ignore */
  }
  notify();
}

/**
 * Clear the stored selection ONLY if it belongs to the given user — a shared
 * machine must never let one user's flow (e.g. an admin visiting the control
 * tower) wipe another user's remembered workspace.
 */
export function clearActiveUnitForUser(userId: string | undefined | null): void {
  const sel = readActiveUnit();
  if (sel && userId && sel.userId !== userId) return;
  writeActiveUnit(null);
}

/** The active unit id for a given user, or null if none / stale / another user's. */
export function activeUnitForUser(userId: string | undefined | null): string | null {
  if (!userId) return null;
  const sel = readActiveUnit();
  return sel && sel.userId === userId ? sel.unitId : null;
}
